import React, {
	type ComponentType,
	memo,
	type MouseEvent,
	type PropsWithChildren,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import { bind } from 'bind-event-listener';
import isEqual from 'lodash/isEqual';
import { useIntl } from 'react-intl';

import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import type { ProviderProps } from '@atlaskit/link-provider';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import UFOSegment from '@atlaskit/react-ufo/segment';
import {
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';
import { extractMarkdownText } from '@atlassian/conversation-assistant-content-transformer/utils/stream-markdown-parser';
import type { LuminaSubAgentResponseType } from '@atlassian/lumina';
import { useActivityClient } from '@atlassian/recent-work-client';
import { SearchDialog } from '@atlassian/search-dialog/search-dialog';
import {
	deleteSessionItem,
	getSessionItem,
	setSessionItem,
} from '@atlassian/search-dialog/session-storage-utils';
import type { SearchExperimentLayer } from '@atlassian/search-experiment/dynamic/types';

import { useSessionId } from '../common/controller/analytics/helpers';
import {
	SAIN_USAGE_DWELL_THRESHOLD,
	SainAction,
	useSAINUsageCriteriaMet,
} from '../common/controller/analytics/hooks/use-sain-usage';
import { useDwellTimer } from '../common/utils/fmp-dwell-timer';
import { getSAINLayerFrontendExperiment } from '../common/utils/get-sain-layer-frontend-experiment';
import { getUnifiedAIExperienceName } from '../common/utils/get-unified-ai-experience-name';
import { SAINFeatureControl } from '../common/utils/sain-feature-control';
import {
	AiBrand,
	CardTemplate,
	DEFAULT_GAS_CHANNEL,
	QueryIntent,
	SEARCH_AI_ANSWER_CACHE_KEY,
} from '../constants';
import { ErrorBoundary } from '../error-boundary';
import { fetchAgentCardData } from '../knowledge-cards/gql/hydrate-agent-card/utils';
import {
	fetchPersonKnowledgeCardDataWithPostFiltering,
	fetchPersonKnowledgeCardDataWithPostFilteringAndRequestId,
} from '../knowledge-cards/gql/hydrate-people-card/utils';
import {
	fetchTeamKnowledgeCardDataWithRanking,
	fetchTeamKnowledgeCardDataWithRankingAndRequestId,
} from '../knowledge-cards/gql/hydrate-team-card/utils';
import { fetchTopicCardData } from '../knowledge-cards/gql/hydrate-topic-card/utils';
import { KnowledgeCard } from '../knowledge-cards/knowledge-card';
import type { IntentClassificationData } from '../query-intent-api/types';
import { getAnswerCardType, isHello, sha256Hash } from '../utils';
import type { DefinitionExperience } from '../v2/definitions/types';
import { SmartAnswerCard as SmartAnswerCardWithTabs } from '../v2/smart-answer-card/smart-answer-card';
import { SmartAnswerCardConsolidated } from '../v2/smart-answer-card/smart-answer-card-consolidated';
import { SmartAnswerCardDynamicLayout } from '../v2/smart-answer-card/smart-answer-card-dynamic-layout';

import { AIAnswerContext, type AIAnswerContextType } from './ai-answer-context';
import { AIAnswerDefaultLoadingWrapper } from './ai-answer-loading/styled';
import {
	onAIAnswerDialogDroppedOff,
	onAIAnswerDialogHidden,
	onAIAnswerDialogQueryIntent,
	onAIAnswerDialogUnmount,
	onAIAnswerDialogViewed,
	onAIAnswerNotShown,
	onAIAnswerResultShown,
	onSmartAnswerRouteDetection,
} from './analytics';
import { DefinitionAnswerCard } from './definition-answer-card/definition-answer-card';
import { aiAnswerDialogMetric, MarkAIAnswerDialogMetricEnd } from './metrics';
import { LegacySmartAnswerCard } from './smart-answer-card/legacy-smart-answer-card';
import { LegacySmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/legacy-smart-answer-card-without-streaming-expand';
import { SmartAnswerCard } from './smart-answer-card/smart-answer-card';
import { SmartAnswerCardWithoutStreamingExpand } from './smart-answer-card/smart-answer-card-without-streaming-expand';
import {
	type AIAnswerCacheType,
	type AIAnswerQueryFunctionType,
	type Callbacks,
	type commonAttributesType,
	type NLPDynamicLayoutSubAgentResponse,
	NLPSearchCustomDefinitionType,
	NLPSearchErrorState,
	type NLPSearchType,
	type parsedFiltersType,
	type ReadingAidsOptions as ReadingAidsOptionsType,
	type selectedFiltersType,
	type TraceHeaders,
} from './types';
import {
	fetchAIAnswer,
	LUMINA_START_DELIMITER,
	LUMINA_END_DELIMITER,
	LUMINA_CONTENT_PREFIX,
	LUMINA_PLACEHOLDER_TAG,
} from './utils/fetchAIAnswer';
import { getKnowledgeCardResultCount } from './utils/knowledgecard-utils';
import { rewriteQueryWithProductFilter } from './utils/rewriteQuery';

const styles = cssMap({
	root: {
		position: 'relative', // this is for absolute positioned children
	},
});

export const DEFAULT_EMPTY_NLP_SEARCH_STATE: NLPSearchType = {
	errorState: NLPSearchErrorState.Default,
	format: null,
	disclaimer: null,
	nlpResults: [],
	uniqueSources: [],
};

export type SearchAIDialogProps = {
	query: string;
	answerLoadingRef?: React.MutableRefObject<boolean>;
	setQuery: (value: string) => void;
	additionalQueryContext?: string;
	cloudId: string;
	orgId?: string;
	getAIAnswer: AIAnswerQueryFunctionType;
	onNavigate: () => void;
	onClose?: () => void;
	advancedSearchUrl?: string;
	searchSessionId: string;
	userDetails: {
		name?: string;
		id?: string;
	};
	wrapper?: ComponentType<PropsWithChildren<any>>;
	loadingWrapper?: ComponentType<PropsWithChildren<any>>;
	source?: string; // Not to be confused with the ai answer sources, this is the source of where package is imported, used for analytics
	isReadingAids?: boolean;
	readingAidsOptions?: ReadingAidsOptionsType;
	extraAnalyticsAttributes?: { [key: string]: any };
	cardProviderProps?: Omit<ProviderProps, 'children'>;
	baseQuery?: string; // used when the query passed in has added pretext (e.g. "what is")
	onLoad?: () => void; // callback to fire when the dialog is loaded (used for popup resize)
	followUpsEnabled?: boolean;
	selectedFilters?: selectedFiltersType;
	parsedFilters?: parsedFiltersType;
	selectedProduct?: string;
	mappedProductFilter?: string;
	edition?: string;
	smartAnswersRoute?: QueryIntent;
	cardTemplate?: CardTemplate;
	sourceProduct?: string; // Product from which source is located e.g. Confluence or Jira
	sourceSubProduct?: string; // Subproduct from which source is located, used for Jira
	workspaceId?: string;
	brand?: AiBrand;
	closeParentDialog?: () => void;
	callbacks?: Callbacks;
	isAdminHubAIEnabled?: boolean;
	readingAidsStreamingExperimentEnabled?: boolean;
	definitionToggleEnabled?: boolean;
	userGeneratedAI?: boolean;
	showChatFollowUps?: boolean;
	definitionExperienceName?: DefinitionExperience;
	showGenericFooter?: boolean;
	initiallyExpanded?: boolean;
	queryIntentClassificationData?: IntentClassificationData;
	experimentContext?: {
		experimentLayers: SearchExperimentLayer[];
	};
	referrer?: string | null;
	traceHeaders?: TraceHeaders;
};

const SearchAIDialogComponent = ({
	query,
	setQuery,
	additionalQueryContext,
	cloudId,
	orgId,
	getAIAnswer,
	onNavigate,
	onClose,
	advancedSearchUrl,
	searchSessionId,
	userDetails,
	wrapper,
	loadingWrapper,
	source = 'unknown',
	isReadingAids = false,
	readingAidsOptions,
	extraAnalyticsAttributes = {},
	cardProviderProps,
	baseQuery = '',
	onLoad,
	followUpsEnabled,
	selectedFilters,
	parsedFilters,
	selectedProduct,
	mappedProductFilter,
	answerLoadingRef,
	smartAnswersRoute,
	cardTemplate = CardTemplate.DEFAULT,
	sourceProduct,
	workspaceId,
	brand = AiBrand.AI,
	closeParentDialog,
	callbacks,
	isAdminHubAIEnabled,
	definitionToggleEnabled,
	showChatFollowUps,
	showGenericFooter = false,
	initiallyExpanded = false,
	experimentContext,
	queryIntentClassificationData,
	referrer,
	traceHeaders,
}: SearchAIDialogProps) => {
	const intl = useIntl();
	const locale = intl.locale;
	const [loading, setLoading] = useState<boolean>(true);
	const [dwellTracked, setDwellTracked] = useState<boolean>(false);
	const dwellTrackedRef = useRef<boolean>(false);
	const partialResponseAbortControllerRef = useRef<AbortController>();
	const [isLuminaAnswer, setIsLuminaAnswer] = useState(false);
	const [luminaContentMap, setLuminaContentMap] = useState<Record<string, string>>({});
	const [partialResponse, setPartialResponse] = useState<AIAnswerContextType['partialResponse']>();
	const [queryReasoning, setQueryReasoning] = useState<AIAnswerContextType['queryReasoning']>();
	const [dynamicLayoutSubAgentMessages, setDynamicLayoutSubAgentMessages] = useState<
		NLPDynamicLayoutSubAgentResponse[] | LuminaSubAgentResponseType[]
	>([]);
	const [tabHasFocus, setTabHasFocus] = useState<boolean>(true);
	const [dwellTimer, setDwellTimer] = useState<NodeJS.Timeout | null>(null);
	const dwellTimerRef = useRef<NodeJS.Timeout | null>(null);
	const definitionDwellTimeRef = useRef<NodeJS.Timeout | null>(null);
	const definitionDwellTimeTrackedRef = useRef<boolean>(false);
	const [answerStreamed, setAnswerStreamed] = useState<boolean>(false);
	const [isFMPReached, setIsFMPReached] = useState<boolean>(false);
	const [isFmpInViewport, setIsFmpInViewport] = useState<boolean>(false);
	const [isAnswerExpanded, setIsAnswerExpanded] = useState<boolean>(initiallyExpanded);

	// FMP-based dwell timer for analytics sizing
	if (fg('kd_fmp_dwell_observer')) {
		// fg check is stable between renders and won't cause issues with hook calls
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDwellTimer({
			isEnabled: isFMPReached,
			isInViewport: isFmpInViewport,
			tabHasFocus,
			thresholdMs: SAIN_USAGE_DWELL_THRESHOLD,
			onDwellStart: () => {
				fireSAINUsageEvent({
					metCriteria: isAnswerExpanded
						? SainAction.FMP_DWELL_START_EXPANDED
						: SainAction.FMP_DWELL_START,
					dwellTime: 0,
					commonAttributes,
				});
			},
			onDwellThreshold: (dwellTime) => {
				fireSAINUsageEvent({
					metCriteria: SainAction.FMP_DWELL_REACHED,
					dwellTime,
					commonAttributes,
					sourceProduct,
				});
			},
			onDwellEnd: (dwellTime) => {
				fireSAINUsageEvent({
					metCriteria: SainAction.FMP_RESULT_EXITED,
					dwellTime,
					commonAttributes,
					sourceProduct,
				});
			},
		});
	}

	const [liked, setLiked] = useState<boolean | undefined>(undefined);
	const [likesDisabled, setLikesDisabled] = useState<boolean>(false);
	const [initialReported, setInitialReported] = useState<boolean | undefined>(undefined);
	const [nlpSearch, setData] = useState<NLPSearchType>(DEFAULT_EMPTY_NLP_SEARCH_STATE);
	const filtersRef = useRef(selectedFilters);
	const selectedProductRef = useRef<string | undefined>(selectedProduct);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const resultRef = useRef<HTMLSpanElement>(null);
	const initialLoadTs = useRef<number>(Date.now()).current;
	const loadedTsRef = useRef<number>();
	const fmpLoadedTsRef = useRef<number>();
	const dwellTimeRef = useRef<number>();

	const sainReverAllExperiment = FeatureGates.getExperimentValue<boolean>(
		'sain_revert_all_experiment',
		'enable',
		false,
	);

	const kdSainTabsExperienceRevert = FeatureGates.getExperimentValue<boolean>(
		'kd_sain_tabs_experience_revert',
		'enable',
		false,
	);

	const { serveExperiment: kdSainSmoothStreamingExperiment } = getSAINLayerFrontendExperiment({
		abTestId: 'kd_sain_smooth_streaming_non_hello_exp',
	});

	const shouldServeTabsExperiment = sainReverAllExperiment || kdSainTabsExperienceRevert;

	const markLoadedTs = () => {
		loadedTsRef.current = Date.now();
		return loadedTsRef.current - initialLoadTs;
	};

	const fmpMarkLoadedTs = () => {
		if (fmpLoadedTsRef.current) {
			return fmpLoadedTsRef.current - initialLoadTs;
		}
	};

	const startViewportDwell = () => {
		dwellTimeRef.current = Date.now();
	};

	const markViewportDwell = () => {
		if (dwellTimeRef.current) {
			return Date.now() - dwellTimeRef.current;
		}
		return 0;
	};

	const { trackAIInteractionInit, trackAIInteractionDismiss } = useAIEventsInstrumentation();
	const sessionId = useSessionId({
		userId: userDetails.id || '',
		cloudId,
		source,
	});

	const activityClient = useActivityClient('v3', 'people');

	const isAnswered = !loading && nlpSearch.errorState === null;
	const hasError =
		!loading && !!nlpSearch.errorState && nlpSearch.errorState !== NLPSearchErrorState.Default;
	const answerString = nlpSearch.nlpResults?.length
		? nlpSearch.nlpResults[0].nlpResult
		: nlpSearch.nlpResult || '';
	const answerFormat = nlpSearch.format;
	const followUps = nlpSearch.nlpFollowUpResults?.followUps;
	const errorStateSuggestedQueries = nlpSearch.nlpSuggestedQueries;
	const sources = nlpSearch.uniqueSources;
	const disclaimer = nlpSearch.disclaimer;
	const answerEditor = nlpSearch.nlpResultEditor;
	const extraAPIAnalyticsAttributes = nlpSearch.extraAPIAnalyticsAttributes;
	const requestId = nlpSearch.extraAPIAnalyticsAttributes?.requestId;
	const answerCardType = getAnswerCardType(isReadingAids, smartAnswersRoute);
	const isHelloValue = isHello(cloudId);

	const commonAttributes: commonAttributesType = useMemo(
		() => ({
			cloudId,
			orgId: orgId ?? '',
			queryHash: sha256Hash(query),
			baseQueryHash: sha256Hash(baseQuery),
			query,
			appliedFilters: parsedFilters,
			searchSessionId,
			source,
			answerString,
			answerLength: answerString.length,
			answerWordCount: answerString.split(/\s+/).length,
			answerFormat,
			answerCardType,
			sources,
			followUps: followUps || [],
			errorStateSuggestedQueries: errorStateSuggestedQueries || [],
			errorState: nlpSearch.errorState,
			errorCode: nlpSearch.errorCode,
			networkErrorReason: nlpSearch.networkErrorReason,
			networkErrorDetails: nlpSearch.networkErrorDetails,
			backendFailureType: nlpSearch.backendFailureType,
			streamEventsReceived: nlpSearch.streamEventsReceived,
			networkErrorDuration: nlpSearch.networkErrorDuration,
			extraAttributes: {
				featureGates: {
					precomputedDefinitionsEnabled: fg('kd_enable_precomputed_definitions'),
				},
				...extraAnalyticsAttributes,
				...extraAPIAnalyticsAttributes,
			},
			apiSource: 'assistance-service',
			workspaceId: workspaceId ?? '',
			sessionId,
			brand,
			sourceProduct,
			isReadingAids,
			queryIntent: smartAnswersRoute,
			isHello: isHelloValue,
		}),
		[
			cloudId,
			orgId,
			query,
			baseQuery,
			parsedFilters,
			searchSessionId,
			source,
			answerString,
			answerFormat,
			nlpSearch.errorState,
			nlpSearch.errorCode,
			nlpSearch.networkErrorReason,
			nlpSearch.networkErrorDetails,
			nlpSearch.backendFailureType,
			nlpSearch.streamEventsReceived,
			nlpSearch.networkErrorDuration,
			extraAnalyticsAttributes,
			extraAPIAnalyticsAttributes,
			workspaceId,
			sessionId,
			brand,
			sourceProduct,
			isReadingAids,
			smartAnswersRoute,
			answerCardType,
			followUps,
			sources,
			errorStateSuggestedQueries,
			isHelloValue,
		],
	);

	const fireAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent(payload).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const onNavigateWithMouseEvent = useCallback(
		(event: MouseEvent) => {
			if (!event.ctrlKey && !event.metaKey) {
				onNavigate();
			}
		},
		[onNavigate],
	);

	const getAIAnswerFn = useCallback(() => {
		switch (smartAnswersRoute) {
			case QueryIntent.PERSON:
				return (_args: any) =>
					fg('kd_fix_sain_mau_instrumentation')
						? fetchPersonKnowledgeCardDataWithPostFilteringAndRequestId({
								tenantId: cloudId,
								personQuery: query,
								sourceProduct,
								workspaceId: workspaceId ?? '',
								traceHeaders,
							})
						: fetchPersonKnowledgeCardDataWithPostFiltering({
								tenantId: cloudId,
								personQuery: query,
								sourceProduct,
								workspaceId: workspaceId ?? '',
								traceHeaders,
							});
			case QueryIntent.TEAM:
				return (_args: any) =>
					fg('kd_fix_sain_mau_instrumentation')
						? fetchTeamKnowledgeCardDataWithRankingAndRequestId({
								activityClient,
								orgId: orgId ?? '',
								siteId: cloudId,
								teamQuery: query,
								sourceProduct,
								workspaceId: workspaceId ?? '',
								traceHeaders,
							})
						: fetchTeamKnowledgeCardDataWithRanking({
								activityClient,
								orgId: orgId ?? '',
								siteId: cloudId,
								teamQuery: query,
								sourceProduct,
								workspaceId: workspaceId ?? '',
								traceHeaders,
							});
			case QueryIntent.TOPIC:
				return (_args: any) =>
					fetchTopicCardData({
						cloudId,
						query,
						workspaceId: workspaceId ?? '',
						sourceProduct,
						traceHeaders,
					});
			case QueryIntent.AGENT:
				return () =>
					fetchAgentCardData({
						query,
						sourceProduct: sourceProduct ?? '',
						limit: 7,
						cloudId,
						workspaceId: workspaceId ?? '',
						traceHeaders,
					});
			case QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY:
				// data fetching is handled by the JiraNlToJqlCard internally,
				// this is to prevent streaming from being called
				setLoading(false);
				return null;
			case QueryIntent.VISUAL:
				// data fetching is handled by the VisualKnowledgeCard internally,
				// this is to prevent streaming from being called
				setLoading(false);
				return null;
			case QueryIntent.ORG_CHART:
				return () =>
					fetchPersonKnowledgeCardDataWithPostFiltering({
						tenantId: cloudId,
						personQuery: query,
						sourceProduct,
						workspaceId: workspaceId ?? '',
						traceHeaders,
					});
			default:
			// fallthrough to getAIAnswer
		}
		return getAIAnswer;
	}, [
		getAIAnswer,
		smartAnswersRoute,
		query,
		orgId,
		cloudId,
		activityClient,
		sourceProduct,
		workspaceId,
		traceHeaders,
	]);

	const updateContextCache = useCallback(
		({
			collapsed,
			liked,
			reported,
			recentWorkListExpanded,
			sourcesExpanded,
			fromFollowUp,
		}: Omit<AIAnswerCacheType, 'query' | 'additionalContext' | 'data'>) => {
			// Do not store session cache if Lumina
			if (isLuminaAnswer && !isReadingAids) {
				return;
			}

			const currentCache = JSON.parse(
				getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}',
			) as AIAnswerCacheType;
			setSessionItem(
				SEARCH_AI_ANSWER_CACHE_KEY,
				JSON.stringify({
					query: query,
					additionalContext: additionalQueryContext || '',
					data: nlpSearch,
					collapsed: collapsed === undefined ? currentCache.collapsed : collapsed,
					liked: liked === undefined ? currentCache.liked : liked,
					reported: reported === undefined ? currentCache.reported : reported,
					fromFollowUp: fromFollowUp === undefined ? currentCache.fromFollowUp : fromFollowUp,
					recentWorkListExpanded:
						recentWorkListExpanded === undefined
							? currentCache.recentWorkListExpanded
							: recentWorkListExpanded,
					sourcesExpanded:
						sourcesExpanded === undefined ? currentCache.sourcesExpanded : sourcesExpanded,
					...(fg('kd_fix_sain_mau_instrumentation')
						? {
								extraAPIAnalyticsAttributes: {
									requestId,
								},
							}
						: {}),
				}),
			);
		},
		[query, additionalQueryContext, nlpSearch, requestId, isReadingAids, isLuminaAnswer],
	);

	const onFMPReached = useCallback(() => {
		setIsFMPReached(true);
		fmpLoadedTsRef.current = Date.now();
	}, []);

	const onAnswerExpandedChange = useCallback((expanded: boolean) => {
		setIsAnswerExpanded(expanded);
	}, []);

	useEffect(() => {
		if (answerLoadingRef) {
			answerLoadingRef.current = !!loading;
		}
	}, [loading, answerLoadingRef]);

	useEffect(() => {
		aiAnswerDialogMetric.start();
		// Reset FMP dwell tracking for new queries
		setIsFMPReached(false);
		setIsFmpInViewport(false);
		if (dwellTimer) {
			clearTimeout(dwellTimer);
			setDwellTimer(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query]);

	const handleIntersect = (entries: IntersectionObserverEntry[]) => {
		entries.forEach((entry) => {
			if (!loading && (isAnswered || hasError)) {
				if (entry.isIntersecting) {
					startViewportDwell();
					fireAnalyticsEvent(
						onAIAnswerDialogViewed({
							...commonAttributes,
						}),
					);
					if (fg('kd_fix_sain_mau_instrumentation')) {
						if (
							smartAnswersRoute !== QueryIntent.TEAM &&
							smartAnswersRoute !== QueryIntent.PERSON &&
							smartAnswersRoute !== QueryIntent.AGENT &&
							!dwellTrackedRef.current
						) {
							dwellTimerRef.current = setTimeout(() => {
								dwellTrackedRef.current = true;
								fireSAINUsageEvent({
									metCriteria: SainAction.DWELL_REACHED,
									dwellTime: markViewportDwell(),
									commonAttributes,
								});
							}, SAIN_USAGE_DWELL_THRESHOLD);
						}
					} else if (
						smartAnswersRoute !== QueryIntent.TEAM &&
						smartAnswersRoute !== QueryIntent.PERSON &&
						!(fg('kd_fix_mau_on_error') && hasError) &&
						!dwellTracked
					) {
						const usageTimer = setTimeout(() => {
							setDwellTracked(true);
							fireSAINUsageEvent({
								metCriteria: SainAction.DWELL_REACHED,
								dwellTime: markViewportDwell(),
								commonAttributes,
							});
						}, SAIN_USAGE_DWELL_THRESHOLD);
						setDwellTimer(usageTimer);

						// DS team (Ala Qabaja & Nan Ma) wants to fire this event
						// if user dwells on Definition for over 3 minutes
						if (
							isReadingAids &&
							!definitionDwellTimeTrackedRef.current &&
							fg('kd_definitions_dwell_fire_result_view')
						) {
							definitionDwellTimeRef.current = setTimeout(() => {
								definitionDwellTimeTrackedRef.current = true;
								fireSAINUsageEvent({
									metCriteria: SainAction.DEFINITION_DWELL_MARKED,
									dwellTime: markViewportDwell(),
									commonAttributes,
								});
							}, 180000);
						}
					}
				} else {
					fireAnalyticsEvent(onAIAnswerDialogHidden({ ...commonAttributes }, markViewportDwell()));
					if (dwellTimerRef.current && fg('kd_fix_sain_mau_instrumentation')) {
						clearTimeout(dwellTimerRef.current);
						dwellTimerRef.current = null;
					}

					// DS team wants to fire this event once user exits viewport
					if (
						isReadingAids &&
						!definitionDwellTimeTrackedRef.current &&
						fg('kd_definitions_dwell_fire_result_view')
					) {
						definitionDwellTimeTrackedRef.current = true;
						// clear timeout to avoid firing duplicate event at 3 minutes
						if (definitionDwellTimeRef.current) {
							clearTimeout(definitionDwellTimeRef.current);
						}
						fireSAINUsageEvent({
							metCriteria: SainAction.DEFINITION_DWELL_MARKED,
							dwellTime: markViewportDwell(),
							commonAttributes,
						});
					}
				}
			}
		});
	};

	useLayoutEffect(() => {
		const observer = new IntersectionObserver(handleIntersect, {
			threshold: 0.3,
		});

		if (resultRef.current && tabHasFocus) {
			observer.observe(resultRef.current);
		}
		return () => {
			observer?.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, resultRef, isAnswered, hasError, tabHasFocus]);

	// FMP dwell intersection observer
	useLayoutEffect(() => {
		// Use different thresholds based on expanded state:
		// - Collapsed: 1.0 (100% in viewport)
		// - Expanded: 0.3 (30% in viewport)
		const threshold = isAnswerExpanded ? 0.3 : 1.0;

		const fmpObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (isFMPReached) {
						// Always update viewport state when FMP is reached, regardless of tab focus
						// Tab focus is handled separately in the dwell timer hook
						setIsFmpInViewport(entry.isIntersecting);
					}
				});
			},
			{
				threshold,
			},
		);

		if (resultRef.current && isFMPReached) {
			fmpObserver.observe(resultRef.current);
		}
		return () => {
			fmpObserver?.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resultRef, isFMPReached, isAnswerExpanded]);

	useEffect(() => {
		const cached = JSON.parse(
			getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY) || '{}',
		) as AIAnswerCacheType;
		if (cached?.query === query && cached?.additionalContext === additionalQueryContext) {
			if (fg('kd_sain_enable_inline_lumina')) {
				const { processedText, boundaryContents } = extractMarkdownText(
					cached.data.nlpResult ?? '',
					LUMINA_START_DELIMITER,
					LUMINA_END_DELIMITER,
					LUMINA_PLACEHOLDER_TAG,
				);

				const luminaContentMap: Record<string, string> = {};
				boundaryContents.forEach((content, index) => {
					luminaContentMap[`${LUMINA_CONTENT_PREFIX}${index}`] = content;
				});

				setLuminaContentMap(luminaContentMap);
				setData({ ...cached.data, nlpResult: processedText });
			} else {
				setData(cached.data);
			}
			if (cached.liked !== undefined) {
				setLiked(cached.liked);
				// if a like is cached, disable likes
				setLikesDisabled(true);
			}
			if (cached.reported !== undefined) {
				setInitialReported(cached.reported);
			}
			setLoading(false);
		} else {
			deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
			setLoading(true);
			setLiked(undefined);
			setLikesDisabled(false);
			fetchAIAnswer({
				cloudId,
				locale,
				query: rewriteQueryWithProductFilter(query, selectedProduct, smartAnswersRoute),
				additional_context: additionalQueryContext,
				source,
				followUpsEnabled: followUpsEnabled && !cached.fromFollowUp,
				getAIAnswer: getAIAnswerFn(),
				setLoading,
				partialResponse,
				partialResponseAbortControllerRef,
				setPartialResponse,
				setData,
				setAnswerStreamed,
				parsedFilters,
				selectedProduct: mappedProductFilter,
				isReadingAids,
				fireAnalyticsEvent,
				setQueryReasoning,
				setDynamicLayoutSubAgentMessages,
				setIsLuminaAnswer,
				setLuminaContentMap,
				experimentContext,
				queryIntentClassificationData,
				referrer,
				isHello: isHelloValue,
				searchSessionId,
				sourceProduct,
				queryIntent: smartAnswersRoute,
			});
		}
		filtersRef.current = selectedFilters;
		if (fg('kd_fix_duplicate_sain_stream_calls')) {
			selectedProductRef.current = selectedProduct;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [query, additionalQueryContext]);

	useEffect(() => {
		if (
			(!fg('kd_hide_sain_for_unsupported_product_filters') &&
				!isEqual(filtersRef.current, selectedFilters)) ||
			!isEqual(selectedProductRef.current, selectedProduct)
		) {
			deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
			setLoading(true);
			setLiked(undefined);
			setLikesDisabled(false);
			if (fg('kd_fix_abort_errors')) {
				setData(DEFAULT_EMPTY_NLP_SEARCH_STATE);
				setPartialResponse(undefined);
			}
			fetchAIAnswer({
				cloudId,
				locale,
				query: rewriteQueryWithProductFilter(query, selectedProduct, smartAnswersRoute),
				source,
				followUpsEnabled,
				getAIAnswer: getAIAnswerFn(),
				setLoading,
				partialResponse,
				partialResponseAbortControllerRef,
				setPartialResponse,
				setData,
				setAnswerStreamed,
				parsedFilters,
				selectedProduct: mappedProductFilter,
				isReadingAids,
				fireAnalyticsEvent,
				setQueryReasoning,
				setDynamicLayoutSubAgentMessages,
				setIsLuminaAnswer,
				setLuminaContentMap,
				experimentContext,
				queryIntentClassificationData,
				referrer,
				isHello: isHelloValue,
				searchSessionId,
				sourceProduct,
				queryIntent: smartAnswersRoute,
			});
		}
		filtersRef.current = selectedFilters;
		selectedProductRef.current = selectedProduct;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFilters, selectedProduct]);

	useEffect(() => {
		if (isAnswered) {
			// if a person or team query returns no data don't log result shown
			const numAddtlWhoMatches =
				smartAnswersRoute === QueryIntent.PERSON
					? getKnowledgeCardResultCount({ answerString, smartAnswersRoute })
					: 0;
			if (
				(smartAnswersRoute === QueryIntent.PERSON ||
					smartAnswersRoute === QueryIntent.TEAM ||
					smartAnswersRoute === QueryIntent.AGENT) &&
				getKnowledgeCardResultCount({ answerString, smartAnswersRoute }) < 1
			) {
				markLoadedTs();
				callbacks?.onAIAnswerAnalyticsFired?.();
				return;
			}
			fireAnalyticsEvent(
				onAIAnswerResultShown({
					...commonAttributes,
					isStreamed: answerStreamed,
					loadTime: markLoadedTs(),
					fmpLoadTime: fmpMarkLoadedTs(),
					experimentLayers: experimentContext?.experimentLayers,
					extraAttributes: {
						...commonAttributes.extraAttributes,
						multipleUserResultsShown: numAddtlWhoMatches > 0,
						numAddtlWhoMatches,
						isCuratedDefinition: !!answerEditor,
						isPrecomputedDefinition:
							nlpSearch.customDefinitionType === NLPSearchCustomDefinitionType.PRECOMPUTED,
					},
				}),
			);
			SAINFeatureControl.triggerNonPrimaryClickTrackingExposure();
			if (numAddtlWhoMatches > 0) {
				fireSAINUsageEvent({
					commonAttributes,
				});
			}
			callbacks?.onAIAnswerAnalyticsFired?.();
		} else if (hasError) {
			fireAnalyticsEvent(
				onAIAnswerNotShown({
					...commonAttributes,
					isStreamed: answerStreamed,
					loadTime: markLoadedTs(),
					fmpLoadTime: fmpMarkLoadedTs(),
				}),
			);
			callbacks?.onAIAnswerAnalyticsFired?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, isAnswered]);

	useLayoutEffect(() => {
		trackAIInteractionInit();
		fireAnalyticsEvent(onAIAnswerDialogQueryIntent(commonAttributes));
		if (!isReadingAids && query && smartAnswersRoute) {
			fireAnalyticsEvent(onSmartAnswerRouteDetection(commonAttributes, smartAnswersRoute));
		}
		return () => {
			fireAnalyticsEvent(onAIAnswerDialogHidden({ ...commonAttributes }, markViewportDwell()));
			fireAnalyticsEvent(
				onAIAnswerDialogUnmount({
					...commonAttributes,
					isReadingAids,
					sourceProduct,
					dwellTime: loadedTsRef.current ? Date.now() - loadedTsRef.current : 0,
					loadTime: markLoadedTs(),
					fmpLoadTime: fmpMarkLoadedTs(),
				}),
			);
			trackAIInteractionDismiss();
			if (!loadedTsRef.current) {
				fireAnalyticsEvent(
					onAIAnswerDialogDroppedOff({
						...commonAttributes,
					}),
				);
			}

			// DS team wants to fire this event once user closes Definition
			if (
				isReadingAids &&
				!definitionDwellTimeTrackedRef.current &&
				fg('kd_definitions_dwell_fire_result_view')
			) {
				definitionDwellTimeTrackedRef.current = true;
				// clear timeout to avoid firing duplicate event at 3 minutes
				if (definitionDwellTimeRef.current) {
					clearTimeout(definitionDwellTimeRef.current);
				}
				fireSAINUsageEvent({
					metCriteria: SainAction.DEFINITION_DWELL_MARKED,
					dwellTime: markViewportDwell(),
					commonAttributes,
				});
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const handleWindowBlurOrClose = () => {
			if (tabHasFocus && !loading && (isAnswered || hasError)) {
				fireAnalyticsEvent(onAIAnswerDialogHidden({ ...commonAttributes }, markViewportDwell()));
				if (dwellTimerRef.current && fg('kd_fix_sain_mau_instrumentation')) {
					clearTimeout(dwellTimerRef.current);
					dwellTimerRef.current = null;
				}

				// DS team wants to fire this event once user leaves tab with Definition still open
				if (
					isReadingAids &&
					!definitionDwellTimeTrackedRef.current &&
					fg('kd_definitions_dwell_fire_result_view')
				) {
					definitionDwellTimeTrackedRef.current = true;
					// clear timeout to avoid firing duplicate event at 3 minutes
					if (definitionDwellTimeRef.current) {
						clearTimeout(definitionDwellTimeRef.current);
					}
					fireSAINUsageEvent({
						metCriteria: SainAction.DEFINITION_DWELL_MARKED,
						dwellTime: markViewportDwell(),
						commonAttributes,
					});
				}
			}
			setTabHasFocus(false);
		};

		const handleWindowFocus = () => {
			setTabHasFocus(true);
			if (
				!dwellTrackedRef.current &&
				!dwellTimerRef.current &&
				fg('kd_fix_sain_mau_instrumentation')
			) {
				dwellTimerRef.current = setTimeout(() => {
					dwellTrackedRef.current = true;
					fireSAINUsageEvent({
						metCriteria: SainAction.DWELL_REACHED,
						dwellTime: markViewportDwell(),
						commonAttributes,
					});
				}, SAIN_USAGE_DWELL_THRESHOLD);
			}
		};

		const removeBlurListener = bind(window, { type: 'blur', listener: handleWindowBlurOrClose });
		const removeFocusListener = bind(window, { type: 'focus', listener: handleWindowFocus });
		const removeCloseListener = bind(window, {
			type: 'beforeunload',
			listener: handleWindowBlurOrClose,
		});

		return () => {
			removeBlurListener();
			removeFocusListener();
			removeCloseListener();
			if (fg('kd_fix_sain_mau_instrumentation')) {
				if (dwellTimerRef.current) {
					clearTimeout(dwellTimerRef.current);
					dwellTimerRef.current = null;
				}
			} else if (dwellTimer) {
				clearTimeout(dwellTimer);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tabHasFocus, loading]);

	useLayoutEffect(() => {
		if (onLoad && !loading && (isAnswered || hasError)) {
			onLoad();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loading, isAnswered, hasError]);

	const AIDialogWrapper = wrapper ? wrapper : SearchDialog;
	const AILoadingWrapper = loadingWrapper ? loadingWrapper : AIAnswerDefaultLoadingWrapper;

	const renderDefinition = () => <DefinitionAnswerCard />;

	// SAIN in FPS
	const renderSearchPageSmartAnswer = () => {
		if (
			(smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY ||
				smartAnswersRoute === QueryIntent.KEYWORD_OR_ACRONYM ||
				(smartAnswersRoute === QueryIntent.NAVIGATIONAL &&
					SAINFeatureControl.isNavigationalIntentEnabled())) &&
			shouldServeTabsExperiment
		) {
			return (
				<AIDialogWrapper>
					{SAINFeatureControl.canRenderLumina() && isLuminaAnswer ? (
						<SmartAnswerCardDynamicLayout
							onFMPReached={onFMPReached}
							onAnswerExpandedChange={onAnswerExpandedChange}
							queryIntent={smartAnswersRoute}
						/>
					) : fg('kd_smooth_streaming_fg') || kdSainSmoothStreamingExperiment ? (
						<SmartAnswerCardConsolidated
							onFMPReached={onFMPReached}
							onAnswerExpandedChange={onAnswerExpandedChange}
							queryIntent={smartAnswersRoute}
						/>
					) : (
						<SmartAnswerCardWithTabs
							onFMPReached={onFMPReached}
							onAnswerExpandedChange={onAnswerExpandedChange}
							queryIntent={smartAnswersRoute}
						/>
					)}
				</AIDialogWrapper>
			);
		} else if (fg('kd_sain_enable_expand_when_streaming')) {
			return (
				<AIDialogWrapper>
					<SmartAnswerCard
						onFMPReached={onFMPReached}
						onAnswerExpandedChange={onAnswerExpandedChange}
					/>
				</AIDialogWrapper>
			);
		} else {
			return (
				<AIDialogWrapper>
					<SmartAnswerCardWithoutStreamingExpand
						onFMPReached={onFMPReached}
						onAnswerExpandedChange={onAnswerExpandedChange}
					/>
				</AIDialogWrapper>
			);
		}
	};

	const renderSearchPageKnowledgeCard = () => (
		<AIDialogWrapper>
			<KnowledgeCard onFMPReached={onFMPReached} />
		</AIDialogWrapper>
	);

	// Quick search and advanced search
	const renderDefaultSmartAnswer = () => {
		return fg('kd_sain_enable_expand_when_streaming') ? (
			<LegacySmartAnswerCard
				LoadingWrapper={AILoadingWrapper}
				DialogWrapper={AIDialogWrapper}
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
			/>
		) : (
			<LegacySmartAnswerCardWithoutStreamingExpand
				LoadingWrapper={AILoadingWrapper}
				DialogWrapper={AIDialogWrapper}
				onFMPReached={onFMPReached}
				onAnswerExpandedChange={onAnswerExpandedChange}
			/>
		);
	};

	const renderExperience = () => {
		switch (smartAnswersRoute) {
			case QueryIntent.NATURAL_LANGUAGE_QUERY:
			case QueryIntent.KEYWORD_OR_ACRONYM:
			case QueryIntent.DATE:
			case QueryIntent.LOCATION:
				return renderSearchPageSmartAnswer();
			case QueryIntent.NAVIGATIONAL:
				return SAINFeatureControl.isNavigationalIntentEnabled()
					? renderSearchPageSmartAnswer()
					: null;
			case QueryIntent.PERSON:
			case QueryIntent.TEAM:
			case QueryIntent.TOPIC:
			case QueryIntent.AGENT:
			case QueryIntent.JIRA_NATURAL_LANGUAGE_QUERY:
			case QueryIntent.ORG_CHART:
			case QueryIntent.VISUAL:
				return renderSearchPageKnowledgeCard();
			default:
				return isReadingAids ? renderDefinition() : renderDefaultSmartAnswer();
		}
	};

	return (
		<AIAnswerContext.Provider
			value={{
				loading,
				partialResponse,
				isAnswered,
				hasError,
				advancedSearchUrl,
				userDetails,
				disclaimer,
				onNavigate: onNavigateWithMouseEvent,
				onClose,
				updateContextCache,
				fireAnalyticsEvent,
				setQuery,
				setData,
				commonAttributes,
				liked,
				setLiked,
				likesDisabled,
				setLikesDisabled,
				initialReported,
				isReadingAids,
				dynamicLayoutSubAgentMessages,
				setDynamicLayoutSubAgentMessages,
				readingAidsOptions,
				cardProviderProps,
				baseQuery,
				cardTemplate,
				smartAnswersRoute,
				queryIntentClassificationData,
				brand,
				answerEditor,
				closeParentDialog,
				additionalQueryContext,
				callbacks,
				isAdminHubAIEnabled,
				definitionToggleEnabled,
				sourceProduct,
				showChatFollowUps,
				queryReasoning,
				showGenericFooter: showGenericFooter ?? false,
				initiallyExpanded: initiallyExpanded ?? false,
				traceHeaders,
				luminaContentMap,
			}}
		>
			<Box xcss={styles.root} ref={resultRef}>
				{renderExperience()}
			</Box>
			{!loading && (isAnswered || hasError) && <MarkAIAnswerDialogMetricEnd />}
		</AIAnswerContext.Provider>
	);
};

export const SearchAIDialog: React.MemoExoticComponent<
	(props: SearchAIDialogProps) => React.JSX.Element
> = memo((props: SearchAIDialogProps): React.JSX.Element => {
	const {
		definitionExperienceName,
		isReadingAids,
		source,
		sourceProduct,
		sourceSubProduct,
		smartAnswersRoute,
		query,
		userGeneratedAI,
	} = props;

	return (
		<UFOSegment name="search-ai-dialog">
			{fg('platform_ufo_custom_cohort_data_migration') ? (
				<>
					<UFOCustomCohortData dataKey="hasSAINComponent" value={true} />
					<UFOCustomCohortData
						dataKey="sainExperienceName"
						value={getUnifiedAIExperienceName(smartAnswersRoute)}
					/>
				</>
			) : (
				<UFOCustomData
					data={{
						hasSAINComponent: true,
						sainExperienceName: getUnifiedAIExperienceName(smartAnswersRoute),
					}}
				/>
			)}
			<ErrorBoundary {...props}>
				<AIEventsInstrumentationProvider
					config={{
						product: sourceProduct ?? 'unknown',
						subproduct: sourceSubProduct ?? 'unknown',
						aiFeatureName: isReadingAids ? 'aiDefinitions' : 'smartAnswers',
						aiExperienceName: isReadingAids
							? definitionExperienceName
							: getUnifiedAIExperienceName(smartAnswersRoute),
						proactiveGeneratedAI: userGeneratedAI ? 0 : 1,
						userGeneratedAI: userGeneratedAI ? 1 : 0,
						isAIFeature: 1,
						source: source,
						channel: DEFAULT_GAS_CHANNEL,
					}}
				>
					{fg('kd_enable_smart_answer_key') || SAINFeatureControl.isSAINUXUpliftEnabled() ? (
						<SearchAIDialogComponent {...props} key={query} />
					) : (
						<SearchAIDialogComponent {...props} />
					)}
				</AIEventsInstrumentationProvider>
			</ErrorBoundary>
		</UFOSegment>
	);
});
